import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Search, BookOpen } from "lucide-react";
import Seo from "@/components/Seo";
import Reveal from "@/components/Reveal";
import { useSite } from "@/lib/SiteContext";
import { api } from "@/lib/api";
import { ShareButtons } from "@/components/Forms";
import NotFound from "./NotFound";

function fmtDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("en-US", {
      year: "numeric", month: "long", day: "numeric", timeZone: "America/New_York",
    });
  } catch {
    return String(iso).slice(0, 10);
  }
}

function ChronicleNo({ n, className = "" }) {
  return (
    <span className={`font-ui text-[11px] uppercase tracking-[0.3em] text-crimson ${className}`}>
      Chronicle No. {n}
    </span>
  );
}

export default function Daily() {
  const { flags } = useSite();
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [theme, setTheme] = useState("");
  const [year, setYear] = useState("");

  useEffect(() => {
    api.get("/public/daily").then((r) => setData(r.data)).catch(() => setData({ featured: null, items: [], facets: { themes: [], years: [] }, count: 0 }));
  }, []);

  const items = data?.items || [];
  const featured = data?.featured || null;
  const rest = useMemo(() => (featured ? items.filter((c) => c.slug !== featured.slug) : items), [items, featured]);

  const filtered = useMemo(() => {
    return rest.filter((c) => {
      if (theme && c.theme !== theme) return false;
      if (year && !(c.published_at || "").startsWith(year)) return false;
      if (q) {
        const hay = `${c.title} ${c.theme} ${(c.scripture_refs || []).join(" ")} ${(c.tags || []).join(" ")}`.toLowerCase();
        if (!hay.includes(q.toLowerCase())) return false;
      }
      return true;
    });
  }, [rest, theme, year, q]);

  if (data !== null && data.count === 0 && (flags.daily || 0) === 0) return <NotFound />;

  return (
    <div className="bg-ink min-h-screen" data-testid="daily-page">
      <Seo title="The Daily Chronicle — Dirty Church Chronicles"
        description="A daily prophetic writing by Jermaine Miller. Truth that confronts, a call to return — new original Chronicles published every day."
        path="/daily" />

      {/* Featured */}
      <section className="pt-40 pb-16 max-w-7xl mx-auto px-6 sm:px-10">
        <Reveal>
          <p className="eyebrow mb-4">The Daily Chronicle</p>
          <h1 className="font-display font-black uppercase text-paper text-4xl sm:text-6xl tracking-tight mb-4">
            Truth, Every Day
          </h1>
          <p className="font-reading text-xl text-paper/70 max-w-2xl mb-14">
            An original prophetic writing by Jermaine Miller, published daily — a permanent archive of conviction, correction, and the call to return.
          </p>
        </Reveal>

        {featured ? (
          <Reveal delay={0.1}>
            <Link to={`/daily/${featured.slug}`} data-testid="daily-featured"
              className="group block border border-paper/12 bg-coal p-8 sm:p-12 transition-colors duration-300 hover:border-crimson/40">
              <div className="flex items-center gap-4 mb-6">
                <ChronicleNo n={featured.number} />
                <span className="font-ui text-[11px] uppercase tracking-[0.25em] text-paper/40">
                  Today · {fmtDate(featured.published_at)}
                </span>
              </div>
              <h2 className="font-display text-3xl sm:text-5xl text-paper leading-tight max-w-4xl group-hover:text-crimsonhi transition-colors duration-300">
                {featured.title}
              </h2>
              {featured.scripture_refs?.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-6">
                  {featured.scripture_refs.map((s) => (
                    <span key={s} className="font-ui text-[11px] uppercase tracking-[0.18em] text-gold border border-gold/30 rounded-full px-3 py-1">{s}</span>
                  ))}
                </div>
              )}
              <span className="inline-flex items-center gap-2 font-ui text-xs uppercase tracking-[0.25em] text-crimson mt-8 group-hover:gap-3 transition-all duration-300">
                Read today's Chronicle <ArrowRight size={14} />
              </span>
            </Link>
          </Reveal>
        ) : (
          <p className="font-ui text-sm text-paper/40 border border-paper/10 p-10" data-testid="daily-empty">
            The first Chronicle is being prepared. Check back soon.
          </p>
        )}
      </section>

      {/* Archive */}
      {rest.length > 0 && (
        <section className="pb-28 max-w-7xl mx-auto px-6 sm:px-10">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 mb-10 border-t border-paper/10 pt-12">
            <h3 className="font-display text-2xl text-paper">The Archive</h3>
            <div className="flex flex-wrap gap-3">
              <div className="relative">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-paper/40" />
                <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search Chronicles"
                  data-testid="daily-search"
                  className="bg-coal border border-paper/15 focus:border-crimson rounded-full pl-9 pr-4 py-2.5 font-ui text-sm text-paper placeholder:text-paper/30 outline-none transition-colors duration-300 w-56" />
              </div>
              {(data?.facets?.themes || []).length > 0 && (
                <select value={theme} onChange={(e) => setTheme(e.target.value)} data-testid="daily-filter-theme"
                  className="bg-coal border border-paper/15 focus:border-crimson rounded-full px-4 py-2.5 font-ui text-sm text-paper outline-none transition-colors duration-300">
                  <option value="">All themes</option>
                  {data.facets.themes.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              )}
              {(data?.facets?.years || []).length > 0 && (
                <select value={year} onChange={(e) => setYear(e.target.value)} data-testid="daily-filter-year"
                  className="bg-coal border border-paper/15 focus:border-crimson rounded-full px-4 py-2.5 font-ui text-sm text-paper outline-none transition-colors duration-300">
                  <option value="">All years</option>
                  {data.facets.years.map((y) => <option key={y} value={y}>{y}</option>)}
                </select>
              )}
            </div>
          </div>

          {filtered.length === 0 ? (
            <p className="font-ui text-sm text-paper/40 py-10" data-testid="daily-no-results">No Chronicles match your search.</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="daily-archive-grid">
              {filtered.map((c, i) => (
                <Reveal key={c.slug} delay={Math.min(i * 0.05, 0.3)}>
                  <Link to={`/daily/${c.slug}`} data-testid={`daily-card-${c.slug}`}
                    className="group flex flex-col h-full border border-paper/10 bg-coal p-7 transition-colors duration-300 hover:border-crimson/40">
                    <div className="flex items-center justify-between mb-4">
                      <ChronicleNo n={c.number} />
                      <span className="font-ui text-[10px] uppercase tracking-[0.2em] text-paper/35">{fmtDate(c.published_at)}</span>
                    </div>
                    <h4 className="font-display text-xl text-paper leading-snug group-hover:text-crimsonhi transition-colors duration-300">{c.title}</h4>
                    {c.theme && <p className="font-ui text-[11px] uppercase tracking-[0.18em] text-paper/45 mt-3">{c.theme}</p>}
                    {c.scripture_refs?.length > 0 && (
                      <p className="font-reading text-sm text-gold/80 mt-auto pt-5">{c.scripture_refs.slice(0, 2).join(" · ")}</p>
                    )}
                  </Link>
                </Reveal>
              ))}
            </div>
          )}
        </section>
      )}
    </div>
  );
}

export function DailyReader() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    setMissing(false);
    setData(null);
    api.get(`/public/daily/${slug}`).then((r) => setData(r.data)).catch(() => setMissing(true));
  }, [slug]);

  if (missing) return <NotFound />;
  if (!data) {
    return (
      <div className="bg-ink min-h-screen pt-40 px-6" data-testid="daily-reader-loading">
        <p className="font-ui text-paper/40 text-sm uppercase tracking-[0.3em] text-center">Opening Chronicle…</p>
      </div>
    );
  }

  const c = data.chronicle;
  const paragraphs = (c.body || "").split(/\n\s*\n/).filter(Boolean);

  return (
    <div className="reader-obsidian min-h-screen" data-testid="daily-reader">
      <Seo title={`${c.title} — The Daily Chronicle`}
        description={`Chronicle No. ${c.number} — an original daily prophetic writing by Jermaine Miller.`}
        path={`/daily/${slug}`}
        jsonLd={{
          "@context": "https://schema.org", "@type": "Article", headline: c.title,
          author: { "@type": "Person", name: "Jermaine Miller" },
          datePublished: c.published_at, publisher: { "@type": "Organization", name: "Dirty Church Chronicles" },
        }} />
      <article className="max-w-3xl mx-auto px-6 sm:px-10 pt-36 pb-24">
        <Link to="/daily" data-testid="daily-reader-back"
          className="inline-flex items-center gap-2 font-ui text-xs uppercase tracking-[0.25em] mb-12 text-paper/60 hover:opacity-70 transition-opacity duration-300">
          <ArrowLeft size={14} /> The Daily Chronicle
        </Link>

        <div className="flex items-center gap-4 mb-5">
          <span className="font-ui text-[12px] uppercase tracking-[0.3em] text-crimson" data-testid="daily-reader-number">Chronicle No. {c.number}</span>
          <span className="font-ui text-[12px] uppercase tracking-[0.2em] text-paper/45">{fmtDate(c.published_at)}</span>
        </div>
        <h1 className="font-display font-bold text-3xl sm:text-5xl leading-tight text-paper mb-6" data-testid="daily-reader-title">{c.title}</h1>

        {c.scripture_refs?.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-10" data-testid="daily-reader-scriptures">
            {c.scripture_refs.map((s) => (
              <span key={s} className="font-ui text-[11px] uppercase tracking-[0.18em] text-gold border border-gold/30 rounded-full px-3 py-1">{s}</span>
            ))}
          </div>
        )}

        <div className="font-reading text-xl leading-relaxed space-y-7 text-paper/85" data-testid="daily-reader-body">
          {paragraphs.map((p, i) => <p key={i}>{p}</p>)}
        </div>

        <p className="font-ui text-xs uppercase tracking-[0.25em] text-paper/45 mt-14 pt-8 border-t border-paper/12">
          Written by {c.author || "Jermaine Miller"}
        </p>

        <div className="mt-8 flex flex-wrap items-center justify-between gap-6 text-paper/70">
          <ShareButtons title={c.title} text={`Chronicle No. ${c.number} — The Daily Chronicle by Jermaine Miller`} path={`/daily/${slug}`} />
          <div className="flex gap-6">
            {data.prev && (
              <Link to={`/daily/${data.prev.slug}`} data-testid="daily-reader-prev"
                className="inline-flex items-center gap-2 font-ui text-xs uppercase tracking-[0.2em] hover:opacity-70 transition-opacity duration-300 max-w-[42vw] truncate">
                <ArrowLeft size={13} /> {data.prev.title}
              </Link>
            )}
            {data.next && (
              <Link to={`/daily/${data.next.slug}`} data-testid="daily-reader-next"
                className="inline-flex items-center gap-2 font-ui text-xs uppercase tracking-[0.2em] hover:opacity-70 transition-opacity duration-300 max-w-[42vw] truncate">
                {data.next.title} <ArrowRight size={13} />
              </Link>
            )}
          </div>
        </div>

        <Link to="/bookstore" data-testid="daily-reader-book-cta"
          className="mt-14 flex items-center gap-3 border border-paper/12 bg-coal/60 p-6 group hover:border-crimson/40 transition-colors duration-300">
          <BookOpen size={20} className="text-crimson shrink-0" />
          <span className="font-reading text-paper/80">Continue in the books — <span className="text-crimsonhi">Dirty Church Chronicles: Volume I</span></span>
          <ArrowRight size={16} className="text-paper/50 ml-auto group-hover:translate-x-1 transition-transform duration-300" />
        </Link>
      </article>
    </div>
  );
}

export function DailyFeature() {
  const [featured, setFeatured] = useState(null);
  useEffect(() => {
    api.get("/public/daily").then((r) => setFeatured(r.data?.featured || null)).catch(() => {});
  }, []);
  if (!featured) return null;
  return (
    <section className="relative bg-ink py-24 sm:py-32 border-y border-paper/10" data-testid="home-daily-section">
      <div className="max-w-7xl mx-auto px-6 sm:px-10">
        <Reveal>
          <p className="eyebrow mb-4">The Daily Chronicle</p>
          <h2 className="font-display font-black uppercase text-paper text-3xl sm:text-5xl tracking-tight mb-10 max-w-3xl">
            A New Word, Every Day
          </h2>
        </Reveal>
        <Reveal delay={0.1}>
          <Link to={`/daily/${featured.slug}`} data-testid="home-daily-featured"
            className="group block border border-paper/12 bg-coal p-8 sm:p-10 transition-colors duration-300 hover:border-crimson/40">
            <span className="font-ui text-[11px] uppercase tracking-[0.3em] text-crimson">Chronicle No. {featured.number}</span>
            <h3 className="font-display text-2xl sm:text-4xl text-paper leading-tight mt-4 max-w-3xl group-hover:text-crimsonhi transition-colors duration-300">
              {featured.title}
            </h3>
            <span className="inline-flex items-center gap-2 font-ui text-xs uppercase tracking-[0.25em] text-crimson mt-7 group-hover:gap-3 transition-all duration-300">
              Read today's Chronicle <ArrowRight size={14} />
            </span>
          </Link>
        </Reveal>
        <Reveal delay={0.15}>
          <Link to="/daily" data-testid="home-daily-archive-link"
            className="inline-flex items-center gap-2 font-ui text-xs uppercase tracking-[0.25em] text-paper/60 mt-8 hover:text-paper transition-colors duration-300">
            Browse the full archive <ArrowRight size={14} />
          </Link>
        </Reveal>
      </div>
    </section>
  );
}
